/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
(function () {
    // Polyfill pour fetch si nécessaire
    function fetchPolyfill(url, options) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open(options.method || 'GET', url);
            
            if (options.headers) {
                Object.keys(options.headers).forEach(key => {
                    xhr.setRequestHeader(key, options.headers[key]);
                });
            }

            xhr.onload = () => {
                resolve({
                    ok: xhr.status >= 200 && xhr.status < 300,
                    status: xhr.status,
                    statusText: xhr.statusText,
                    json: () => Promise.resolve(JSON.parse(xhr.responseText))
                });
            };

            xhr.onerror = () => reject(new Error('Network request failed'));
            xhr.send(options.body);
        });
    }

    // Utiliser le fetch natif ou le polyfill
    const safeFetch = window.fetch ? window.fetch.bind(window) : fetchPolyfill;

    const CONFIG = {
        COOKIE_NAME: 'utm_data',
        COOKIE_EXPIRATION_DAYS: 5
    };

    function safeGetUTMFromURL() {
        try {
            const search = window.location.search.substring(1);
            const params = {};
            search.split('&').forEach(param => {
                const [key, value] = param.split('=').map(decodeURIComponent);
                if (key.startsWith('utm_')) {
                    params[key] = value || null;
                }
            });
            return {
                utm_source: params.utm_source || null,
                utm_medium: params.utm_medium || null,
                utm_campaign: params.utm_campaign || null,
                utm_content: params.utm_content || null,
                utm_term: params.utm_term || null
            };
        } catch (e) {
            return {};
        }
    }

    function safeGetCookie(name) {
        try {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) {
                const cookieValue = parts.pop().split(';').shift();
                return decodeURIComponent(cookieValue);
            }
            return null;
        } catch (e) {
            return null;
        }
    }

    function safeSetCookie(name, value, days) {
        try {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            const expires = `expires=${date.toUTCString()}`;
            document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/`;
            return true;
        } catch (e) {
            return false;
        }
    }

    function safeUpdateCart(attributes, note) {
        return safeFetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes, note })
        })
        .then(response => response.json())
        .catch(error => {
            // Retry once with XMLHttpRequest if fetch fails
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('POST', '/cart/update.js');
                xhr.setRequestHeader('Content-Type', 'application/json');
                xhr.onload = () => resolve(JSON.parse(xhr.responseText));
                xhr.onerror = () => reject(error);
                xhr.send(JSON.stringify({ attributes, note }));
            });
        });
    }

    function updateCartWithUTM() {
        try {
            // Get UTM data
            let utmData = safeGetUTMFromURL();
            const hasUTMFromURL = Object.values(utmData).some(value => value !== null);

            if (!hasUTMFromURL) {
                const utmCookie = safeGetCookie(CONFIG.COOKIE_NAME);
                if (utmCookie) {
                    try {
                        utmData = JSON.parse(utmCookie);
                    } catch (e) {
                        utmData = {};
                    }
                }
            } else {
                safeSetCookie(CONFIG.COOKIE_NAME, JSON.stringify(utmData), CONFIG.COOKIE_EXPIRATION_DAYS);
            }

            // Get basic data
            const referrer = document.referrer || 'direct';
            const currentPath = window.location.pathname || '/';

            // Clean UTM data
            const cleanUtmData = Object.fromEntries(
                Object.entries(utmData).filter(([_, v]) => v != null && v !== '')
            );

            // Prepare cart data
            const cartAttributes = {
                ...cleanUtmData,
                current_url: currentPath,
                referrer: referrer
            };

            const note = [
                `current_url:${currentPath}`,
                ...Object.entries(cleanUtmData).map(([k, v]) => `${k}:${v}`)
            ].join(', ');

            // Update cart
            return safeFetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                    const updatedAttributes = {
                        ...cart.attributes,
                        ...cartAttributes
                    };
                    return safeUpdateCart(updatedAttributes, note);
                })
                .catch(() => {
                    // Fallback si fetch échoue
                    return safeUpdateCart(cartAttributes, note);
                });
        } catch (error) {
            // Même en cas d'erreur, on ne bloque pas l'expérience utilisateur
            console.error('UTM tracking error:', error);
        }
    }

    // Double exécution avec délai différent
    setTimeout(updateCartWithUTM, 100);  // Rapide
    setTimeout(updateCartWithUTM, 2000); // Plus lent en backup
})();
